<script>
import {
    fade
} from 'svelte/transition';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    environment
} from '@app/store/envStore.svelte.js';
import {
    styleState
} from '@app/store/styleStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import EventGroup from '@cclib/classes/eventGroup.js';
import LanguageSelect from '@app/components/inputs/LanguageSelect.svelte';
import EventGroupStandardsForm from '@app/components/inputs/EventGroupStandardsForm.svelte';
import insertEvent from '@cclib/functions/eventGroups/insertEvent.js';
import orderByTime from '@cclib/functions/eventGroups/orderByTime.js';
import { v4 as uuidv4 } from 'uuid';
import * as m from '@app/paraglide/messages.js';
import { AUDIO_DUBBING, hasOriginalText } from '@app/lib/eventGroupTypes.js';
let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
let eventGroupType = $state(eventGroupDefaults.eventGroupType || "subtitle"),
    displayName = $state(""),
    language = $state(eventGroupDefaults.language || "en-US"),
    rtl = $state(eventGroupDefaults.rtl),
    fontFamily = $state(eventGroupDefaults.fontFamily || "plexMono"),
    fontSize = $state(eventGroupDefaults.fontSize || 16),
    overlap = $state(eventGroupDefaults.overlap),
    maxCps = $state(eventGroupDefaults.maxCps || 25),
    maxWpm = $state(eventGroupDefaults.maxWpm || 250),
    maxChars = $state(eventGroupDefaults.maxChars || 32),
    maxLines = $state(eventGroupDefaults.maxLines || 2),
    minDuration = $state(eventGroupDefaults.minDuration || 1),
    maxDuration = $state(eventGroupDefaults.maxDuration || 12),
    illegalChars = $state(eventGroupDefaults.illegalChars),
    linkedGroupId = $state(), //Translation Types only
    allowOriginalEdit = $state(false),
    multiviewGroupIds = $state([]), //Multiview Types only - selection order determines track order
    canCreateAudioDescription = $derived(authState.ad || !environment.online);

function toggleMultiviewGroup(groupId) {
    if (multiviewGroupIds.includes(groupId)) {
        multiviewGroupIds = multiviewGroupIds.filter((id) => id !== groupId);
    } else {
        multiviewGroupIds = [...multiviewGroupIds, groupId];
    }
}

function createEventGroup() {
    let multiviewSourceGroups = eventGroupType === 'multiview'
        ? multiviewGroupIds
              .map((id) => eventGroupState.find((evg) => evg.id === id))
              .filter(Boolean)
        : [];

    let eventGroupOptions = {
        type: eventGroupType,
        tracks: multiviewSourceGroups.map((evg) => ({
            name: evg.name,
            language: evg.language,
            rtl: evg.rtl
        })),
        originalFontFamily: styleState.fontFamily,
        originalRtl : rtl,
        name: displayName,
        language: language,
        linkedGroup: linkedGroupId,
        allowOriginalEdit: hasOriginalText(eventGroupType) ? !!allowOriginalEdit : false,
        rtl: rtl,
        fontFamily: fontFamily,
        fontSize: fontSize || 16,
        illegalChars : illegalChars,
        overlap: overlap,
        maxCps: maxCps || 25,
        maxWpm: maxWpm || 250,
        maxChars: maxChars || 32,
        maxLines: maxLines || 2,
        minDuration : minDuration || 1,
        maxDuration : maxDuration || 12
    }

    // Create a new event group and push it to the state
    const newEventGroup = {...(new EventGroup(eventGroupOptions))};
    eventGroupState.push(newEventGroup);
    
    projectState.selected = eventGroupState.length - 1;
    
    if (eventGroupType === 'multiview') {
        multiviewSourceGroups.forEach((sourceGroup, trackIndex) => {
            sourceGroup.events.forEach((event) => {
                /* Fresh ids - source groups can share event ids (e.g. translation + its linked group) */
                eventGroupState[projectState.selected].events.push({
                    ...event,
                    id: uuidv4(),
                    track: trackIndex + 1
                });
            });
        });

        orderByTime(eventGroupState[projectState.selected]);
    } else if (eventGroupType === 'translation') {
        let linkedGroupIndex = eventGroupState.findIndex(evg =>{
            return evg.id === linkedGroupId;
        });

        if (linkedGroupIndex > -1){
            eventGroupState[linkedGroupIndex].events.forEach(event => {
                // Push each event to the events array instead of reassigning
                eventGroupState[projectState.selected].events.push({
                    ...event,
                    original : event.text,
                    text : ""
                });
            });

            eventGroupState[projectState.selected].originalRtl = eventGroupState[projectState.selected].rtl;
        }        
    } else if (eventGroupType === AUDIO_DUBBING) {
        let linkedGroup = eventGroupState.find(evg => evg.id === linkedGroupId);

        if (linkedGroup) {
            /* A translation already holds source and target text, so the dub can be rendered right away */
            const fromTranslation = linkedGroup.type === "translation";
            linkedGroup.events.forEach(event => {
                const text = fromTranslation ? event.text : "";
                eventGroupState[projectState.selected].events.push({
                    ...event,
                    original : fromTranslation ? event.original : event.text,
                    text,
                    speed : 1,
                    audioFile : "",
                    audioFileUuid : false,
                    audioFileRemote : "",
                    waveformFile : "",
                    audioFileDuration : false,
                    peaksData : [],
                    peaksSampleRate : 0,
                    rendered : !text
                });
            });

            eventGroupState[projectState.selected].originalRtl = fromTranslation ? !!linkedGroup.originalRtl : !!linkedGroup.rtl;
            if (fromTranslation && linkedGroup.originalFontFamily) {
                eventGroupState[projectState.selected].originalFontFamily = linkedGroup.originalFontFamily;
            }
        }
    } else if (eventGroupType !== 'subtitle' && eventGroupType !== 'translation') {
        // Instead of direct assignment, store the result and update specific properties
        const result = insertEvent(eventGroupState[projectState.selected], {
            alignment: "left"
        });
        
        // Copy the events array from the result to the existing event group
        eventGroupState[projectState.selected].events = [...result.events];
        
        // Copy any other properties that might have changed
        if (result.selected) {
            eventGroupState[projectState.selected].selected = [...result.selected];
        }
    } else {
        // Instead of direct assignment, store the result and update specific properties
        const result = insertEvent(eventGroupState[projectState.selected], {
            alignment: "center"
        });
        
        // Copy the events array from the result to the existing event group
        eventGroupState[projectState.selected].events = [...result.events];
        
        // Copy any other properties that might have changed
        if (result.selected) {
            eventGroupState[projectState.selected].selected = [...result.selected];
        }
    }

    localStorage.setItem("cc-event-group-defaults", JSON.stringify({
        eventGroupType: eventGroupType,
        name: displayName,
        language: language,
        fontFamily: fontFamily,
        fontSize: fontSize || 16,
        overlap : overlap,
        illegalChars : illegalChars,
        maxCps : maxCps || 25,
        maxWpm : maxWpm || 250,
        maxChars : maxChars || 32,
        maxLines : maxLines || 2,  
        minDuration : minDuration >= 0 ? minDuration : 0.5,
        maxDuration : maxDuration || 12,
        rtl: rtl
    }));

    insertHistoryAction({
        name: "insert event group", //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });
    hideModal();
}
</script>

<div transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'newEventGroup' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="newEventGroupTitle"
    aria-describedby="newEventGroupDescription" 
    tabindex="-1" 
    id="NewEventGroupModal">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="newEventGroupTitle">{m.new_event_group()}</h4>
                <button type="button" class="btn-close" id="newEventGroupCloseBtn" aria-label={m.close_new_event_group_modal()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <form onsubmit={() => createEventGroup()} id="NewEventGroupForm">
                    <!-- Group Type -->
                    <div class="mb-3">
                        <label class="form-label" for="groupTypeSelect">{m.group_type()}</label>
                        <select class="form-select" id="groupTypeSelect" bind:value={eventGroupType} aria-describedby="groupTypeDescription">
                            <option value="subtitle">{m.subtitle()}</option>
                            {#if canCreateAudioDescription}
                            <option value="audio description">{m.audio_description()}</option>
                            <option value="audio dubbing">{m.audio_dubbing()}</option>
                            {/if}
                            <option value="transcription">{m.transcription()}</option>
                            <option value="translation">{m.translation()}</option>
                            <option value="multiview">{m.multiview()}</option>
                        </select>
                    </div>
                    <!-- Name -->
                    <div class="mb-3"><label class="form-label" for="displayNameInput">{m.display_name()}</label>
                        <input class="form-control" 
                               id="displayNameInput"
                               required 
                               placeholder={m.display_name()} 
                               type="text" 
                               bind:value={displayName}
                               aria-required="true"
                               aria-invalid={!displayName}>
                    </div>
                    <!-- Language -->
                    <div class="mb-3">
                        <label class="form-label" for="Language">{m.language()}</label>
                        <LanguageSelect id="Language" bind:value={language} />
                    </div>
                    <div class="form-check form-switch mb-2">
                        <input type="checkbox" class="form-check-input" id="rtlSwitch" bind:checked={rtl}>
                        <label class="form-check-label" for="rtlSwitch">{m.right_to_left()}</label>
                    </div>
                    <EventGroupStandardsForm
                        heading={m.error_detection()}
                        idPrefix="new-event-group"
                        bind:maxCps
                        bind:maxWpm
                        bind:maxChars
                        bind:maxLines
                        bind:minDuration
                        bind:maxDuration
                        bind:overlap
                        bind:illegalChars />
                    <!-- Linked Group -->
                    {#if hasOriginalText(eventGroupType)}
                    <div transition:fade={{duration: 100 }} role="region" aria-labelledby="translationOptionsTitle">
                        <hr/>
                        <p class="lead text-warning m-0" id="translationOptionsTitle">{m.translation_options()}</p>
                        <div class="mb-3">
                            <label class="form-label" for="linkedGroupSelect">
                                <span class="badge bg-danger">{m.required()}</span> {m.linked_group()} 
                                <i class="bi bi-info-circle" aria-label={m.linked_group_help()}></i>
                            </label>
                            <select class="form-select" 
                                    id="linkedGroupSelect"  
                                    bind:value={linkedGroupId}
                                    aria-required="true"
                                    aria-invalid={hasOriginalText(eventGroupType) && !linkedGroupId}>
                                {#each eventGroupState as eventGroup (eventGroup.id)}
                                <option value={eventGroup.id}>{eventGroup.name}</option>
                                {/each}
                            </select>
                        </div>
                        <div class="form-check form-switch mb-2">
                            <input type="checkbox" class="form-check-input" id="allowOriginalEditSwitch" bind:checked={allowOriginalEdit}>
                            <label class="form-check-label" for="allowOriginalEditSwitch">{m.edit_event_group_allow_original_edit_label()}</label>
                        </div>
                        <div class="form-text mb-2">{m.edit_event_group_allow_original_edit_help()}</div>
                    </div>
                    {/if}
                    <!-- Multiview Source Groups -->
                    {#if eventGroupType === 'multiview'}
                    <div transition:fade={{duration: 100 }} role="region" aria-labelledby="multiviewOptionsTitle">
                        <hr/>
                        <p class="lead text-warning m-0" id="multiviewOptionsTitle">{m.multiview_options()}</p>
                        <div class="mb-3">
                            <label class="form-label" for="multiviewGroupList">
                                <span class="badge bg-danger">{m.required()}</span> {m.multiview_source_groups()}
                                <i class="bi bi-info-circle" aria-label={m.multiview_source_groups_help()}></i>
                            </label>
                            <div class="form-text mb-2">{m.multiview_source_groups_help()}</div>
                            <div id="multiviewGroupList" class="border rounded p-2 multiview-group-list" role="group" aria-label={m.multiview_source_groups()}>
                                {#each eventGroupState as eventGroup (eventGroup.id)}
                                <div class="form-check">
                                    <input type="checkbox"
                                           class="form-check-input"
                                           id="multiviewGroup-{eventGroup.id}"
                                           checked={multiviewGroupIds.includes(eventGroup.id)}
                                           onchange={() => toggleMultiviewGroup(eventGroup.id)}>
                                    <label class="form-check-label" for="multiviewGroup-{eventGroup.id}">
                                        {eventGroup.name}
                                        {#if multiviewGroupIds.includes(eventGroup.id)}
                                        <span class="badge bg-primary ms-1">{m.multiview_track()} {multiviewGroupIds.indexOf(eventGroup.id) + 1}</span>
                                        {/if}
                                    </label>
                                </div>
                                {/each}
                            </div>
                            {#if multiviewGroupIds.length < 2}
                            <div class="form-text text-warning">{m.multiview_min_groups_warning()}</div>
                            {/if}
                        </div>
                    </div>
                    {/if}
                </form>
            </div>
            <div class="modal-footer">
                <button class="btn btn-primary" 
                        id="createGroupBtn"
                        type="button" 
                        disabled={(hasOriginalText(eventGroupType) && linkedGroupId === undefined) || (eventGroupType === 'multiview' && multiviewGroupIds.length < 2)} 
                        onclick={() => createEventGroup()}
                        aria-label={m.create_new_event_group()}>
                    {m.create_group()}
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="css">
.multiview-group-list {
    max-height: 200px;
    overflow-y: auto;
}
</style>
